<template>
  <div :class="rootClasses">
    <div v-if="$slots?.label || $slots?.helperText" class="mb-2">
      <h4 v-if="$slots.label" class="font-medium" :id="$attrs.id ? `${attrs.id}-label` : undefined">
        <slot name="label" />
      </h4>
      <span v-if="$slots.helperText">
        <slot name="helperText" />
      </span>
    </div>
    <template v-if="loading">
      <Skeleton v-for="skeleton in 4" :key="skeleton" height="51px" class="mb-2" />
    </template>
    <draggable
      v-else
      id="reorder-list"
      v-model="value"
      v-bind="$attrs"
      tag="ul"
      class="pl-0 my-0"
      @start="drag = true"
      @end="onDragEnd()"
      :item-key="itemKey"
      :disabled="disabled || !sortable"
      :aria-labelledby="$attrs.id ? `${attrs.id}-label` : undefined"
    >
      <template #item="{ element }">
        <li
          class="drag-item flex justify-content-start align-items-center mb-2 px-3 py-1 border-round-lg cursor-pointer w-full"
          :class="{
            'drag-item-highlight': isSelected(element),
            'drag-item-disabled': disabled,
            'drag-item-deleted': isElementDeleted(element),
          }"
          @pointerdown="select(element)"
          @pointerup="selected = null"
          :draggable="sortable && !isElementDeleted(element)"
          :aria-label="`${getOptionLabel(element)}`"
        >
          <i
            class="drag-button material-symbols-rounded p-datatable-reorderablerow-handle mr-3"
            aria-hidden="true"
            v-if="sortable"
            >drag_indicator</i
          >
          <span class="text-sm flex-grow-1">{{ getOptionLabel(element) }}</span>

          <slot name="custom-action" :element="element" />

          <div class="ml-auto">
            <IconButton
              v-if="!isElementDeleted(element)"
              class="remove-button c-icon-button"
              :class="{ 'remove-button--visible': removeable && isElementRemovable(element) }"
              @click="removeItem(element)"
              :aria-label="`Remove ${getOptionLabel(element)}`"
            >
              <template #icon>
                <i class="material-symbols-rounded" aria-hidden="true"> remove_circle_outline </i>
              </template>
            </IconButton>
            <IconButton
              v-if="isElementDeleted(element) && restorable"
              class="restore-button c-icon-button"
              :class="{ 'restore-button--visible': restorable }"
              @click="restoreItem(element)"
              :aria-label="`Restore ${getOptionLabel(element)}`"
            >
              <template #icon>
                <i class="material-symbols-rounded" aria-hidden="true"> add_circle_outline </i>
              </template>
            </IconButton>
          </div>
        </li>
      </template>
    </draggable>
  </div>
</template>
<script lang="ts" setup>
import { ref, useAttrs, type PropType } from 'vue';
import draggable from 'vuedraggable';
import { IconButton } from '../../buttons/icon-button';
import { Skeleton } from '../../misc/skeleton';
import { useInputState } from '../../controls';
import { isEqual } from 'lodash-es';

defineOptions({
  inheritAttrs: false,
});

const props = defineProps({
  loading: {
    type: Boolean,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  itemKey: {
    type: String,
    default: 'id',
  },
  optionLabel: {
    type: String,
    default: 'name',
  },
  removeable: {
    type: Boolean,
    default: false,
  },
  isElementRemovable: {
    type: Function,
    default: (_value: any) => true,
  },
  isElementDeleted: {
    type: Function,
    default: (_value: any) => false,
  },
  restorable: {
    type: Boolean,
    default: false,
  },
  shouldSoftDeleteElement: {
    type: Function,
    default: (_value: any) => false,
  },
  sortable: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['removeItem', 'restoreItem', 'select', 'onDragEnd']);

const value = defineModel({ type: Array as PropType<any[]>, default: (): any[] => [] });

const drag = ref(false);

const selected = ref();
const select = (element: any) => {
  if (!props.sortable) {
    return;
  }
  selected.value = element;
  emit('select');
};

const isSelected = (element: any): boolean => {
  if (selected?.value && isEqual(element, selected.value)) {
    return true;
  }
  return false;
};

const onDragEnd = async () => {
  drag.value = false;
  selected.value = null;
  emit('onDragEnd');
};
const removeItem = (element: any) => {
  if (props.restorable && props.shouldSoftDeleteElement(element)) {
    // Soft delete: set deleted_at instead of removing
    value.value = value.value.map((item: any) => {
      if (item?.[props.itemKey] === element?.[props.itemKey]) {
        return {
          ...item,
          deleted_at: new Date(),
        };
      }
      return item;
    });
  } else {
    // Hard delete: remove from array
    value.value = value.value?.filter((item) => {
      return item?.[props.itemKey] !== element?.[props.itemKey];
    });
  }
  emit('removeItem', element);
};

const restoreItem = (element: any) => {
  // Restore: remove deleted_at field
  value.value = value.value.map((item: any) => {
    if (item?.[props.itemKey] === element?.[props.itemKey]) {
      return {
        ...item,
        deleted_at: null,
      };
    }
    return item;
  });
  emit('restoreItem', element);
};

function getOptionLabel(elementObject: any): string | undefined {
  const segments = props.optionLabel.split('.');
  let current: any = elementObject;
  for (const segment of segments) {
    if (!(segment in current)) return undefined;
    current = current[segment];
  }
  return current;
}

const attrs = useAttrs();

const { classes: rootClasses } = useInputState(attrs);
</script>
<style scoped lang="scss">
.drag-item {
  background: $shade000;
  border: solid 1px $shade400;
  color: #000000;
  .remove-button {
    color: #6c757d;
    opacity: 0;
    pointer-events: none;
    &.remove-button--visible {
      opacity: 1;
      pointer-events: auto;
    }
  }
  &.drag-item-highlight {
    font-weight: 600;
    color: $highlightTextColor;
    background: #eff6ff;
  }
  &.drag-item-disabled {
    pointer-events: none;
  }
  &.drag-item-deleted {
    background-color: $shade200 !important;
    color: $shade600 !important;
    opacity: 0.7;
  }
  .drag-button {
    color: $shade600;
  }
  .restore-button {
    color: #6c757d;
    opacity: 0;
    pointer-events: none;
    &.restore-button--visible {
      opacity: 1;
      pointer-events: auto;
    }
  }
  :focus {
    background: #c5dbff;
  }
}
</style>
